import React, { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../hooks/useAuth';
import { getSessionLogs } from '../services/firestoreService';
import { DECKS } from '../data/stimuli';
import '../styles/data.css';

function formatDate(raw) {
  if (!raw) return '';
  const d = raw?.toDate ? raw.toDate() : raw?.seconds ? new Date(raw.seconds * 1000) : new Date(raw);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

function accuracyColor(pct) {
  if (pct >= 80) return '#16a34a';
  if (pct >= 60) return '#D97706';
  return '#DC2626';
}

function accuracyBg(pct) {
  if (pct >= 80) return '#22C55E';
  if (pct >= 60) return '#F59E0B';
  return '#EF4444';
}

export const DataPage = () => {
  const { currentUser } = useAuth();
  const [sessions, setSessions]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [deckFilter, setDeckFilter] = useState('all');

  useEffect(() => {
    if (!currentUser) { setLoading(false); return; }
    getSessionLogs(currentUser.uid).then(res => {
      if (res.success) setSessions(res.data);
      setLoading(false);
    });
  }, [currentUser]);

  // Unique deck names that appear in session history
  const deckNames = useMemo(() => [...new Set(sessions.map(s => s.deckName))], [sessions]);

  const filtered = deckFilter === 'all' ? sessions : sessions.filter(s => s.deckName === deckFilter);

  // Per-card stats aggregated across filtered sessions
  const cardStats = useMemo(() => {
    const stats = {};
    filtered.forEach(session => {
      (session.cardResults || []).forEach(({ label, correct }) => {
        if (!label) return;
        if (!stats[label]) stats[label] = { correct: 0, total: 0 };
        stats[label].total++;
        if (correct) stats[label].correct++;
      });
    });
    return Object.entries(stats)
      .map(([label, { correct, total }]) => ({
        label,
        correct,
        total,
        pct: Math.round((correct / total) * 100),
      }))
      .sort((a, b) => b.total - a.total);
  }, [filtered]);

  const missedCards   = [...cardStats].sort((a, b) => a.pct - b.pct).filter(s => s.pct < 80).slice(0, 8);
  const correctCards  = [...cardStats].sort((a, b) => b.pct - a.pct).filter(s => s.pct >= 80).slice(0, 8);

  // Overall stats
  const totalSessions = filtered.length;
  const avgAccuracy   = filtered.length > 0
    ? Math.round(filtered.reduce((sum, s) => sum + (s.accuracy ?? 0), 0) / filtered.length)
    : null;
  const totalTrials = filtered.reduce((sum, s) => sum + (s.total ?? 0), 0);

  return (
    <Layout>
      <div className="data-page">
        <div className="page-header">
          <h1 className="deck-page-title">Session Log</h1>
          <p>Track accuracy and progress across all sessions</p>
        </div>

        {/* ── Deck filter ── */}
        <div className="data-filter-row">
          <button
            className={`data-filter-btn${deckFilter === 'all' ? ' active' : ''}`}
            onClick={() => setDeckFilter('all')}
          >
            All Decks
          </button>
          {deckNames.map(name => {
            const deck = DECKS.find(d => d.name === name);
            return (
              <button
                key={name}
                className={`data-filter-btn${deckFilter === name ? ' active' : ''}`}
                onClick={() => setDeckFilter(name)}
              >
                {deck?.emoji} {name}
              </button>
            );
          })}
        </div>

        {/* ── Overview stats ── */}
        {totalSessions > 0 && (
          <div className="data-overview-row">
            <div className="data-overview-card">
              <span className="data-overview-value">{totalSessions}</span>
              <span className="data-overview-label">Sessions</span>
            </div>
            <div className="data-overview-card">
              <span className="data-overview-value">{totalTrials}</span>
              <span className="data-overview-label">Total Trials</span>
            </div>
            {avgAccuracy !== null && (
              <div className="data-overview-card">
                <span className="data-overview-value" style={{ color: accuracyColor(avgAccuracy) }}>{avgAccuracy}%</span>
                <span className="data-overview-label">Avg Accuracy</span>
              </div>
            )}
          </div>
        )}

        {/* ── Session history ── */}
        <div className="page-card">
          <div className="page-card-title">Recent Sessions</div>
          <hr className="page-card-divider" />

          {loading ? (
            <div className="data-loading">Loading session data…</div>
          ) : filtered.length === 0 ? (
            <div className="data-empty">
              <span className="data-empty-icon">📊</span>
              <p>No sessions logged yet.</p>
              <p className="data-empty-sub">Complete a session to see your data here.</p>
            </div>
          ) : (
            <div className="data-sessions-list">
              {filtered.map(session => {
                const deck = DECKS.find(d => d.name === session.deckName);
                const pct  = session.accuracy ?? (session.total > 0 ? Math.round((session.correct / session.total) * 100) : 0);
                return (
                  <div key={session.id} className="data-session-row">
                    <div className="data-session-left">
                      <span className="data-session-emoji">{deck?.emoji || '🃏'}</span>
                      <div className="data-session-info">
                        <span className="data-session-name">{session.deckName}</span>
                        <span className="data-session-date">{formatDate(session.completedAt)}</span>
                      </div>
                    </div>
                    <div className="data-session-right">
                      <div className="data-session-bar-wrap">
                        <div className="data-session-bar">
                          <div
                            className="data-session-bar-fill"
                            style={{ width: `${pct}%`, background: accuracyBg(pct) }}
                          />
                        </div>
                        <span className="data-session-pct" style={{ color: accuracyColor(pct) }}>{pct}%</span>
                      </div>
                      <span className="data-session-detail">{session.correct ?? '—'} / {session.total ?? '—'} correct</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Card performance ── */}
        {cardStats.length > 0 && (
          <div className="data-perf-grid">
            {/* Commonly missed */}
            <div className="page-card">
              <div className="page-card-title">❌ Commonly Missed</div>
              <hr className="page-card-divider" />
              {missedCards.length === 0 ? (
                <p className="data-perf-empty">No commonly missed cards — great work!</p>
              ) : (
                missedCards.map(s => (
                  <div key={s.label} className="data-perf-row">
                    <span className="data-perf-label">{s.label}</span>
                    <div className="data-perf-bar-wrap">
                      <div className="data-perf-bar">
                        <div className="data-perf-fill data-perf-fill--incorrect" style={{ width: `${s.pct}%` }} />
                      </div>
                      <span className="data-perf-pct" style={{ color: accuracyColor(s.pct) }}>{s.pct}%</span>
                    </div>
                    <span className="data-perf-detail">{s.correct}/{s.total}</span>
                  </div>
                ))
              )}
            </div>

            {/* Commonly correct */}
            <div className="page-card">
              <div className="page-card-title">✓ Commonly Correct</div>
              <hr className="page-card-divider" />
              {correctCards.length === 0 ? (
                <p className="data-perf-empty">Keep practicing to see mastered cards here.</p>
              ) : (
                correctCards.map(s => (
                  <div key={s.label} className="data-perf-row">
                    <span className="data-perf-label">{s.label}</span>
                    <div className="data-perf-bar-wrap">
                      <div className="data-perf-bar">
                        <div className="data-perf-fill data-perf-fill--correct" style={{ width: `${s.pct}%` }} />
                      </div>
                      <span className="data-perf-pct" style={{ color: accuracyColor(s.pct) }}>{s.pct}%</span>
                    </div>
                    <span className="data-perf-detail">{s.correct}/{s.total}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
};
